import {
  collection, doc, addDoc, setDoc, updateDoc, onSnapshot, query, where,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase-config';

/**
 * 1:1 video calls between chat users — WebRTC with FIRESTORE AS SIGNALING
 * (the standard Firebase pattern): no media server, no new backend. The two
 * peers exchange an offer/answer through a call doc and trickle ICE
 * candidates through two subcollections; the media itself flows peer-to-peer
 * (relayed by TURN only when NAT blocks the direct path).
 *
 *   calls/{callId}
 *     chatId, callerUid, callerName, calleeUid, calleeName
 *     status: 'ringing' | 'accepted' | 'declined' | 'ended' | 'missed'
 *     offer {type,sdp}, answer {type,sdp}, createdAtMs, endedAtMs
 *   calls/{callId}/callerCandidates/*   — caller's ICE candidates
 *   calls/{callId}/calleeCandidates/*   — callee's ICE candidates
 *
 * ICE servers: Google's free STUN, plus a TURN relay for the ~15% of
 * connections carrier NAT blocks. THE PLAN (user decision 2026-08-31) is
 * coturn on the Muuzbox Hetzner server — once it's provisioned (see
 * docs/coturn-setup.sh in this repo), fill HETZNER_TURN below and delete
 * the Open Relay interim entries. Only ICE_SERVERS ever needs editing.
 */

// Fill when coturn is live on the Hetzner box, e.g.:
//   { urls: ['turn:turn.muuzbox.com:3478', 'turns:turn.muuzbox.com:5349'],
//     username: 'muuzbox', credential: '<the static-auth secret user>' }
const HETZNER_TURN = null;

export const CALL_EVENT = 'muuzbox:start-call';

/** How long an unanswered ring lasts before the caller marks it missed. */
export const RING_TIMEOUT_MS = 45000;

const ICE_SERVERS = {
  iceServers: [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
    ...(HETZNER_TURN ? [HETZNER_TURN] : [
      // INTERIM ONLY until the Hetzner coturn is live — free public relay,
      // no SLA. Delete both entries when HETZNER_TURN is filled.
      { urls: 'turn:openrelay.metered.ca:80', username: 'openrelayproject', credential: 'openrelayproject' },
      { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' },
    ]),
  ],
  iceCandidatePoolSize: 10,
};

const MEDIA = { video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, audio: true };

/** Ask ChatV2 (or anywhere) to start a call — CallManager listens app-wide. */
export const requestCall = (detail) => {
  try { window.dispatchEvent(new CustomEvent(CALL_EVENT, { detail })); } catch (_) {}
};

/** Camera+mic, or a clear error string the UI can show. */
export const getLocalStream = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('This browser cannot access the camera.');
  }
  return navigator.mediaDevices.getUserMedia(MEDIA);
};

const newPeer = (localStream, onRemoteStream) => {
  const pc = new RTCPeerConnection(ICE_SERVERS);
  localStream.getTracks().forEach((t) => pc.addTrack(t, localStream));
  const remote = new MediaStream();
  pc.ontrack = (ev) => {
    ev.streams[0]?.getTracks().forEach((t) => remote.addTrack(t));
    onRemoteStream(remote);
  };
  return pc;
};

/**
 * CALLER: create the call, ring the callee. Returns handles the UI owns.
 * `onRemoteStream` fires when the callee's video arrives; `unsubs` must all
 * be called on teardown.
 */
export const startCall = async ({ chatId, calleeUid, calleeName, callerName, localStream, onRemoteStream }) => {
  const uid = auth.currentUser?.uid;
  if (!uid || !calleeUid) throw new Error('Not signed in.');
  const pc = newPeer(localStream, onRemoteStream);

  const callRef = await addDoc(collection(db, 'calls'), {
    chatId: chatId || null,
    callerUid: uid, callerName: callerName || 'Guest',
    calleeUid, calleeName: calleeName || 'Guest',
    status: 'ringing', offer: null, answer: null,
    createdAtMs: Date.now(), createdAt: serverTimestamp(), endedAtMs: null,
  });

  pc.onicecandidate = (ev) => {
    if (ev.candidate) {
      addDoc(collection(db, 'calls', callRef.id, 'callerCandidates'), ev.candidate.toJSON()).catch(() => {});
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await updateDoc(callRef, { offer: { type: offer.type, sdp: offer.sdp } });

  // Answer + remote candidates flow back through the doc.
  const unsubs = [];
  unsubs.push(onSnapshot(callRef, async (snap) => {
    const data = snap.data();
    if (data?.answer && !pc.currentRemoteDescription) {
      try { await pc.setRemoteDescription(new RTCSessionDescription(data.answer)); } catch (_) {}
    }
  }));
  unsubs.push(onSnapshot(collection(db, 'calls', callRef.id, 'calleeCandidates'), (snap) => {
    snap.docChanges().forEach((ch) => {
      if (ch.type === 'added') {
        pc.addIceCandidate(new RTCIceCandidate(ch.doc.data())).catch(() => {});
      }
    });
  }));

  return { callId: callRef.id, pc, unsubs };
};

/** CALLEE: accept a ringing call. Same handle shape as startCall. */
export const answerCall = async ({ call, localStream, onRemoteStream }) => {
  const pc = newPeer(localStream, onRemoteStream);
  const callRef = doc(db, 'calls', call.id);

  pc.onicecandidate = (ev) => {
    if (ev.candidate) {
      addDoc(collection(db, 'calls', call.id, 'calleeCandidates'), ev.candidate.toJSON()).catch(() => {});
    }
  };

  await pc.setRemoteDescription(new RTCSessionDescription(call.offer));
  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);
  await updateDoc(callRef, { answer: { type: answer.type, sdp: answer.sdp }, status: 'accepted' });

  const unsubs = [];
  unsubs.push(onSnapshot(collection(db, 'calls', call.id, 'callerCandidates'), (snap) => {
    snap.docChanges().forEach((ch) => {
      if (ch.type === 'added') {
        pc.addIceCandidate(new RTCIceCandidate(ch.doc.data())).catch(() => {});
      }
    });
  }));

  return { callId: call.id, pc, unsubs };
};

/** Either side finishes (or declines / marks missed) — one status write. */
export const setCallStatus = async (callId, status) => {
  if (!callId) return;
  await updateDoc(doc(db, 'calls', callId), { status, endedAtMs: Date.now() }).catch(() => {});
};

/** Watch ONE call doc (status flips: accepted / declined / ended). */
export const watchCall = (callId, cb) =>
  onSnapshot(doc(db, 'calls', callId), (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null));

/**
 * Watch for calls RINGING ME. Equality-only query — no composite index
 * needed. Stale docs (an old ring nobody cleaned up) are filtered by age.
 */
export const watchIncomingCalls = (uid, cb) => {
  if (!uid) return () => {};
  const qy = query(collection(db, 'calls'), where('calleeUid', '==', uid), where('status', '==', 'ringing'));
  return onSnapshot(qy, (snap) => {
    const rows = [];
    snap.forEach((d) => {
      const x = { id: d.id, ...d.data() };
      if ((Date.now() - (x.createdAtMs || 0)) < RING_TIMEOUT_MS + 15000) rows.push(x);
    });
    rows.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
    cb(rows[0] || null);
  }, (e) => console.warn('[calls] incoming listener:', e?.message));
};

/** Full teardown — safe to call twice. */
export const teardown = ({ pc, unsubs, localStream }) => {
  (unsubs || []).forEach((u) => { try { u(); } catch (_) {} });
  try { pc?.close(); } catch (_) {}
  try { localStream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
};
